import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * React hook for opening file selector and handling uploaded files.
 * @param {Object} [options]
 * @param {'image'|'audio'|'video'|'pdf'|'any'} [options.allowedType] - Allowed file type at hook level.
 * @param {boolean} [options.allowMultiple=false] - Allow multiple file selection at hook level.
 * @param {(files: File[]) => void} [options.onFilesChanged] - Callback when selected files change.
 * @returns {{
 *   files: File[];
 *   openDialog: (config?: { allowedType?: 'image'|'audio'|'video'|'pdf'|'any'; allowMultiple?: boolean; }) => void;
 *   reset: () => void
 * }}
 */
function useUploader({
  allowedType,
  allowMultiple = false,
  onFilesChanged,
} = {}) {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const fileTypeMap = {
    image: ["image/*"],
    audio: ["audio/*"],
    video: ["video/*"],
    pdf: ["application/pdf"],
    any: ["*/*"],
  };

  useEffect(() => {
    return () => {
      if (fileInputRef.current) {
        try {
          fileInputRef.current?.removeEventListener("change", handleChange);
          document.body.removeChild(fileInputRef.current);
        } catch (error) {
          console.log(error)
          console.warn(
            `useUploader could not remove the input element. Most likely the input element has already been unmounted.`, fileInputRef.current
          );
        }
      }
    };
  }, []);

  function createInput() {
    if (fileInputRef.current) return fileInputRef.current;
    console.log("creating input");
    const input = document.createElement("input");
    input.type = "file";
    input.style.display = "none";
    document.body.appendChild(input);
    fileInputRef.current = input;
    input.addEventListener("change", handleChange);
    return input;
  }

  const handleChange = (event) => {
    const selected = Array.from(event.target.files);
    setFiles(selected);
    if (typeof onFilesChanged === "function") {
      // console.log('Changed here...')
      onFilesChanged(selected);
    }
  };

  function reset() {
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  /**
   * Open the file dialog.
   * @param {Object} [config]
   * @param {'image'|'audio'|'video'|'pdf'} [config.allowedType] - Override allowed file type.
   * @param {boolean} [config.allowMultiple] - Override multiple selection.
   */
  const openDialog = useCallback(
    (config = {}) => {
      const input = createInput();
      input.value = "";
      const type = config.allowedType || allowedType;
      const multiple =
        typeof config.allowMultiple === "boolean"
          ? config.allowMultiple
          : allowMultiple;

      if (type && fileTypeMap[type]) {
        input.accept = fileTypeMap[type].join(",");
      } else {
        input.removeAttribute("accept");
      }
      input.multiple = multiple;
      input.click();
    },
    [allowedType, allowMultiple]
  );

  return { files, openDialog, onFilesChanged, reset };
}

export default useUploader;
